/**
 * OpenStreetMap Integration using Leaflet.js
 */

export function initPropertyMap() {
    const mapContainer = document.getElementById('property-map');

    if (!mapContainer) {
        return;
    }

    const lat = parseFloat(mapContainer.dataset.lat);
    const lng = parseFloat(mapContainer.dataset.lng);
    const address = mapContainer.dataset.address || '';

    if (isNaN(lat) || isNaN(lng)) {
        console.error('Invalid map coordinates');
        return;
    }

    // Check if Leaflet is loaded
    if (typeof L === 'undefined') {
        console.error('Leaflet library not loaded. Please include Leaflet CSS and JS.');
        return;
    }

    // Initialize the map with zoom controls enabled
    const map = L.map('property-map', {
        zoomControl: true,
        attributionControl: true
    }).setView([lat, lng], 16);

    // Base layer with no labels (CartoDB Light - matching fifty-fifty block)
    const baseLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19
    }).addTo(map);

    // Only add major labels (streets and city names)
    const labelsLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png', {
        attribution: '',
        subdomains: 'abcd',
        maxZoom: 19,
        pane: 'shadowPane'
    }).addTo(map);

    // Enable interactions to match fifty-fifty block
    map.dragging.enable();
    map.touchZoom.enable();
    map.doubleClickZoom.enable();
    map.scrollWheelZoom.disable();

    // Create custom icon
    const customIcon = L.icon({
        iconUrl: '/wp-content/themes/aspinallverdi/assets/images/icon_location.svg',
        iconSize: [27.5, 38.5],
        iconAnchor: [25, 35],
        popupAnchor: [1, -34]
    });

    // Add marker
    const marker = L.marker([lat, lng], { icon: customIcon }).addTo(map);

    // Add popup if address exists
    if (address) {
        marker.bindPopup(`<strong>${address}</strong>`).openPopup();
    }

    // Fix map display issues on load
    setTimeout(() => {
        map.invalidateSize();
    }, 100);
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPropertyMap);
} else {
    initPropertyMap();
}

/**
 * Load Leaflet library if not already loaded
 */
function loadLeaflet() {
    return new Promise((resolve, reject) => {
        // Check if Leaflet is already loaded
        if (typeof L !== 'undefined') {
            resolve();
            return;
        }

        // Load CSS
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        css.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        css.crossOrigin = 'anonymous';
        document.head.appendChild(css);

        // Load JavaScript
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = 'anonymous';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Leaflet'));
        document.head.appendChild(script);
    });
}

/**
 * Leaflet Map for Fifty-Fifty Block
 */
export function initFiftyFiftyMaps() {
    // Find all map containers
    const mapContainers = document.querySelectorAll('.acf-map');

    if (mapContainers.length === 0) {
        return;
    }

    // Load Leaflet library first
    loadLeaflet().then(() => {
        mapContainers.forEach(function(container) {
            // Skip if already initialized
            if (container.dataset.mapInitialized) {
                return;
            }
            container.dataset.mapInitialized = 'true';

            // Create map instance
            const map = L.map(container, {
                zoomControl: false,
                attributionControl: true
            }).setView([0, 0], container.dataset.zoom || 16);

        // Base layer with no labels
        const baseLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: 'abcd',
            maxZoom: 19
        }).addTo(map);

        // Only add major labels (streets and city names)
        const labelsLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png', {
            attribution: '',
            subdomains: 'abcd',
            maxZoom: 19,
            pane: 'shadowPane'
        }).addTo(map);

        // Enable interactions
        map.dragging.enable();
        map.touchZoom.enable();
        map.doubleClickZoom.enable();
        map.scrollWheelZoom.disable();

        // Find all markers
        const markers = container.querySelectorAll('.marker');
        const markerGroup = L.featureGroup();

        // Add markers to map
        markers.forEach(function(markerElem) {
            const lat = parseFloat(markerElem.dataset.lat);
            const lng = parseFloat(markerElem.dataset.lng);

            if (lat && lng) {
                // Create custom icon
                const customIcon = L.icon({
                    iconUrl: '/wp-content/themes/aspinallverdi/assets/images/icon_location.svg',
                    iconSize: [27.5, 38.5],
                    iconAnchor: [25, 35],
                });

                // Add marker to map
                const marker = L.marker([lat, lng], { icon: customIcon }).addTo(map);
                markerGroup.addLayer(marker);

                // Add popup if content exists
                if (markerElem.innerHTML.trim() !== '') {
                    marker.bindPopup(markerElem.innerHTML);
                }
            }
        });

        // Center map based on markers
        if (markerGroup.getLayers().length > 0) {
            map.fitBounds(markerGroup.getBounds());
        }

        // For single marker, set a specific zoom level
        if (markerGroup.getLayers().length === 1) {
            map.setZoom(container.dataset.zoom || 16);
        }
        });
    }).catch(error => {
        console.error('Error initializing fifty-fifty maps:', error);
    });
}

// Initialize fifty-fifty maps on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFiftyFiftyMaps);
} else {
    initFiftyFiftyMaps();
}
